import PropTypes from "prop-types"
import classnames from "classnames"
import { map } from "lodash"
import { CardContent, Paper, Tooltip as MuiTooltip } from "@mui/material"
import { OfflineBolt } from "@mui/icons-material"
import { DATE } from "helpers/shapes"
import { useTrack } from "helpers/analytics"
import MultiLine from "components/charts/multi-line"
import MultiLineOverlapping from "components/charts/multi-line-overlapping"
import Tooltip from "components/analyze-tooltip"
import { numberFormatter, oneDecimalPercentageFormatter, autoFormatter, smallPercentageFormatter } from "helpers/formatters/numbers"
import NoData from "components/no-data"
import NoBetas from "components/no-betas"
import AnalyzeBetaDetails from "modules/analyze-composite-beta-details"
import AnalyzeConcentrationDetails from "modules/analyze-composite-concentration-details"
import { betasAreMissing } from "selectors/missingDataFiles"
import BreakdownTooltip from "components/breakdown-tooltip"
import GraphTopBar from "components/graph-top-bar"
import ExcelDownload from "components/excel-download"
import { computeCSVData } from "helpers/csv"
import { VIEW_TYPES } from "helpers/base"
import "./style.scss"

export default function AnalyzeCompositeComposition({
  analyzeViewSelectList,
  account,
  modelId,
  positionSetId,
  positionSetType,
  positionSetName,
  base,
  baseSignature,
  segment,
  classifications,
  earliestDate,
  latestDate,
  setDate,
  selectedDate,
  startDate,
  endDate,
  data,
  beta,
  items,
  expand,
  allCategoryItems,
  factorCategoryId,
  options,
  toggle,
  toggleOnly,
  selectFactorCategory,
  focusChartLine,
  blurChartLine,
  focusedChartLine,
  viewType,
  drift,
  fileName,
  firstDate,
  selectedOption,
  only,
  showNotHeld,
  factorCategoryName,
  filtersVisible,
  missingDataFiles,
  refEquityOverride,
  setGrouping,
  currentGrouping,
  currency,
}) {
  const trackExcelDownload = useTrack({
    type: "composite-analyze-composition-graph-timeseries-download",
  })

  const ACTIVE_TOOLTIP_MSG = `
    Although a comparison is currently provided,
    this portfolio's concentration is displayed in absolute terms.`

  let graph
  let rightPanel

  if (!earliestDate || !latestDate) {
    graph = <NoData title={factorCategoryName} className="graph" />
  }

  if (factorCategoryId === "beta") {
    if (betasAreMissing(missingDataFiles, modelId)) {
      graph = <NoBetas modelId={modelId} />
    } else {
      graph = <MultiLine
        tooltip={<Tooltip data={data} items={items} focusedChartLine={focusedChartLine} formatter={numberFormatter} />}
        {...{
          startDate,
          date: selectedDate,
          setDate,
          endDate,
          data,
          onlyItem: only,
          items,
          toggle,
          focusChartLine,
          blurChartLine,
          focusedChartLine,
          toggleOnly: (factorId) => toggleOnly(factorId, only !== factorId, items),
        }}
      />
      rightPanel = <AnalyzeBetaDetails {...{
        account,
        modelId,
        positionSetId,
        positionSetType,
        positionSetName,
        base,
        baseSignature,
        segment,
        classifications,
        selectedDate,
        beta,
        missingDataFiles,
        allCategoryItems,
        viewType,
        factorCategoryId,
        factorCategoryName,
        startDate: firstDate,
        endDate,
        expand,
        drift,
        selectedOption,
        showNotHeld,
        setGrouping,
        currentGrouping,
        filtersVisible,
      }}
      />
    }
  } else {
    graph = <MultiLineOverlapping
      tooltip={<BreakdownTooltip
        data={data}
        items={items}
        totalKey="positionsCount"
        formatters={{
          maxConcentration: smallPercentageFormatter,
          topConcentration: smallPercentageFormatter,
          positionsCount: (value) => value,
        }}
      />}
      {...{
        totalTickFormat: autoFormatter,
        tickFormat: oneDecimalPercentageFormatter,
        startDate,
        date: selectedDate,
        setDate,
        endDate,
        data,
        onlyItem: only,
        totalLabel: "Positions",
        breakdownLabel: `Concentration (% of ${refEquityOverride ? "Ref Equity" : "GMV"})`,
        items,
        toggle,
        focusChartLine,
        blurChartLine,
        focusedChartLine,
        toggleOnly: (factorId) => toggleOnly(factorId, only !== factorId, items),
      }}
    />
    rightPanel = <AnalyzeConcentrationDetails {...{
      account,
      modelId,
      positionSetId,
      positionSetType,
      positionSetName,
      segment,
      classifications,
      selectedDate,
      allCategoryItems,
      viewType,
      factorCategoryId,
      factorCategoryName,
      startDate: firstDate,
      endDate,
      drift,
      selectedOption,
      setGrouping,
      currentGrouping,
      filtersVisible,
      currency,
      expand,
    }}
    />
  }

  return <div className="modules_analyze-composite-composition">
    <div className="composition-wrapper">
      <CardContent className={classnames("card-content")}>
        <Paper className="content">
          <div className="graph-container">
            <div className="graph-top">
              {analyzeViewSelectList}
              <GraphTopBar {...{
                value: factorCategoryId,
                onTabChanged: (_, factorId) => selectFactorCategory(factorId),
                tabs: map(options, ({ id, label }) => ({
                  key: id,
                  value: id,
                  label,
                  tooltip: viewType === VIEW_TYPES.ACTIVE && id === "concentration"
                    ? <MuiTooltip title={ACTIVE_TOOLTIP_MSG}>
                      <OfflineBolt className="warning" />
                    </MuiTooltip> : null,
                })),
                actions: [<ExcelDownload key="excel-download" data={computeCSVData(data, items)} fileName={fileName} track={trackExcelDownload} />],
              }}
              />
            </div>
            <div className="graph">
              {graph}
            </div>
          </div>
          <div className="contributors">
            {rightPanel}
          </div>
        </Paper>
      </CardContent>
    </div>
  </div>
}

AnalyzeCompositeComposition.propTypes = {
  analyzeViewSelectList: PropTypes.element,
  account: PropTypes.string.isRequired,
  modelId: PropTypes.string.isRequired,
  positionSetId: PropTypes.string.isRequired,
  positionSetType: PropTypes.string.isRequired,
  positionSetName: PropTypes.string.isRequired,
  segment: PropTypes.object,
  base: PropTypes.object,
  baseSignature: PropTypes.string,
  classifications: PropTypes.array,
  earliestDate: DATE,
  latestDate: DATE,
  startDate: DATE,
  endDate: DATE,
  selectedDate: DATE,
  data: PropTypes.array.isRequired,
  beta: PropTypes.array.isRequired,
  items: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
    disabled: PropTypes.bool.isRequired,
  })).isRequired,
  expand: PropTypes.bool,
  setGrouping: PropTypes.func.isRequired,
  currentGrouping: PropTypes.object,
  allCategoryItems: PropTypes.array.isRequired,
  factorCategoryId: PropTypes.string.isRequired,
  factorCategoryName: PropTypes.string.isRequired,
  viewType: PropTypes.string.isRequired,
  setDate: PropTypes.func.isRequired,
  selectFactorCategory: PropTypes.func.isRequired,
  toggle: PropTypes.func.isRequired,
  focusChartLine: PropTypes.func.isRequired,
  blurChartLine: PropTypes.func.isRequired,
  addNotification: PropTypes.func.isRequired,
  drift: PropTypes.object.isRequired,
  focusedChartLine: PropTypes.string,
  fileName: PropTypes.string.isRequired,
  firstDate: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    color: PropTypes.string.isRequired,
  })).isRequired,
  selectedOption: PropTypes.object.isRequired,
  toggleOnly: PropTypes.func.isRequired,
  only: PropTypes.string,
  showNotHeld: PropTypes.bool.isRequired,
  filtersVisible: PropTypes.bool.isRequired,
  missingDataFiles: PropTypes.object.isRequired,
  refEquityOverride: PropTypes.bool,
  currency: PropTypes.string,
}
